import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { distinctUntilKeyChanged, map, catchError } from 'rxjs/operators';
import {
  NEW_BILL_DOWNLOADER_ENTITY,
  BILL_DOWNLOADER_ENTITY,
} from '../config/my-bill-entity.config';
import { EntityManager } from '@dch/entity-manager';
import { CALL_DETAILS_ENTITY } from '../call-details/config/entities';

export enum DocType {
  PDF = 'pdf',
  XML = 'xml',
}

// Types
export interface DownloadResponse {
  fileResponse?: ArrayBuffer;
  entityUrl?: string;
  version?: string;
  error?: boolean;
  errorMessage?: string;
  errorCode?: string;
}

interface V1Parameters {
  cdbId?: number;
  paId: string;
  documentId: string;
  documentDate: string;
}

interface V2Parameters {
  documentId: string;
  docType: string;
}

interface V2RequestBody {
  requestContext: {
    requestorDetails: {
      registeredEndUserIdentifier: {
        id: number;
      };
    };
  };
  requestDetails: {
    cdbId: number;
    paId: string;
  };
}

export interface DownloadOptions {
  paId: string;
  documentId: string;
  docType?: string;
  documentDate?: string;
  cdbid?: number;
  useV2?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FileDownloaderService {
  /**
   * Downloads file using V1 API (legacy)
   * @param paId - PA (Billing Account) ID
   * @param documentId - Document identifier
   * @param documentDate - Document date
   * @param cdbid - Customer database ID (optional)
   * @returns Observable with download response
   */
  getDownloadedFileV1(
    paId: string,
    documentId: string,
    documentDate: string,
    cdbid?: number
  ): Observable<DownloadResponse> {
    const parameters: V1Parameters = {
      cdbId: cdbid,
      paId: paId.trim(),
      documentId: documentId.trim(),
      documentDate: encodeURIComponent(documentDate.trim()),
    };

    return EntityManager.getEntity<ArrayBuffer>(
      BILL_DOWNLOADER_ENTITY,
      parameters
    ).pipe(
      distinctUntilKeyChanged('value'),
      map((entity) => this.mapEntityToResponse(entity)),
      catchError((error) => this.handleDownloadError(error))
    );
  }

  /**
   * Downloads file using V2 API (new)
   * @param paId - PA (Billing Account) ID
   * @param documentId - Document identifier
   * @param docType - Document type ('pdf' | 'xml')
   * @param cdbid - Customer database ID (required)
   * @returns Observable with download response
   */
  getDownloadedFileV2(
    paId: string,
    documentId: string,
    docType: string,
    cdbid: number
  ): Observable<DownloadResponse> {
    const parameters: V2Parameters = {
      documentId: documentId.trim(),
      docType: docType.toLowerCase(),
    };

    const requestBody: V2RequestBody = this.buildV2RequestBody(
      cdbid,
      paId.trim()
    );

    return EntityManager.saveEntity<ArrayBuffer>({
      ...EntityManager.createEntity(NEW_BILL_DOWNLOADER_ENTITY, parameters),
      value: requestBody,
    }).pipe(
      distinctUntilKeyChanged('value'),
      map((entity) => this.mapEntityToResponse(entity)),
      catchError((error) => this.handleDownloadError(error))
    );
  }

  /**
   * Generic download method with improved options handling
   * @param options - Download options object
   * @returns Observable with download response
   */
  getDownloadedFile(options: DownloadOptions): Observable<DownloadResponse> {
    const {
      paId,
      documentId,
      docType = DocType.PDF,
      documentDate,
      cdbid,
      useV2 = true,
    } = options;
    if (this.shouldUseV2Api(useV2, cdbid)) {
      return this.getDownloadedFileV2(paId, documentId, docType, cdbid!);
    } else {
      return this.getDownloadedFileV1(paId, documentId, documentDate!, cdbid);
    }
  }

  /**
   * Downloads call details file
   */
  getCallDetailsFile(
    options: DownloadOptions
  ): Observable<{ filecontent: string; hasError: boolean }> {
    return EntityManager.getEntity<string>(CALL_DETAILS_ENTITY, {
      cdbId: options.cdbid,
      billingAccountNumber: options.paId,
      documentId: options.documentId,
    }).pipe(
      distinctUntilKeyChanged('value'),
      map((entity) => {
        if (entity?.value) {
          return {
            filecontent: entity?.value || '',
            hasError: false,
          };
        } else {
          return {
            filecontent: '',
            hasError: false,
          };
        }
      }),
      catchError(() =>
        of({
          filecontent: '',
          hasError: true,
        })
      )
    );
  }

  /**
   * Legacy method signature for backward compatibility
   */
  getDownloadedFileLegacy(
    paId: string,
    documentId: string,
    docType: string = DocType.PDF,
    documentDate?: string,
    cdbid?: number,
    useV2: boolean = true
  ): Observable<DownloadResponse> {
    return this.getDownloadedFile({
      paId,
      documentId,
      docType,
      documentDate,
      cdbid,
      useV2,
    });
  }

  /**
   * Validates if the download response is successful
   * @param response - Download response to validate
   * @returns boolean indicating if response is valid
   */
  isValidResponse(response: DownloadResponse): boolean {
    return (
      !response.error &&
      !!response.fileResponse &&
      response.fileResponse.byteLength > 0
    );
  }

  /**
   * Gets the file size of downloaded content
   * @param response - Download response
   * @returns File size in bytes or null if not available
   */
  getFileSize(response: DownloadResponse): number | null {
    return response.fileResponse?.byteLength || null;
  }

  /**
   * Gets human-readable file size
   * @param response - Download response
   * @returns Formatted file size string
   */
  getFormattedFileSize(response: DownloadResponse): string {
    const size = this.getFileSize(response);
    if (!size) return 'Unknown size';

    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let fileSize = size;

    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex++;
    }

    return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
  }

  // Private helper methods

  private shouldUseV2Api(useV2: boolean, cdbid?: number): boolean {
    return useV2 && !!cdbid && cdbid > 0;
  }

  private mapEntityToResponse(entity: any): DownloadResponse {
    if (!entity?.value) {
      return {
        error: true,
      };
    }

    return {
      fileResponse: entity.value,
      entityUrl: entity.url,
      version: entity.version,
    };
  }

  private buildV2RequestBody(cdbid: number, paId: string): V2RequestBody {
    return {
      requestContext: {
        requestorDetails: {
          registeredEndUserIdentifier: {
            id: cdbid,
          },
        },
      },
      requestDetails: {
        cdbId: cdbid,
        paId,
      },
    };
  }

  private handleDownloadError(error: any): Observable<DownloadResponse> {
    return of({
      error: true,
      errorMessage: error?.message,
      errorCode: error?.errorData?.code,
    });
  }
}
